import React, { useState, useEffect } from 'react';
import { 
    Card, 
    Button, 
    Table, 
    Tag, 
    Space, 
    message, 
    Modal,
    Row,
    Col,
    Typography,
    Alert,
    Timeline,
    Badge,
    Divider,
    Statistic
} from 'antd';
import { 
    CreditCardOutlined, 
    CalendarOutlined, 
    DollarOutlined, 
    CheckCircleOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    EyeOutlined
} from '@ant-design/icons';
import api from '../../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const QuotePaymentViewer = ({ quote, onPaymentSuccess }) => {
    const [paymentLinks, setPaymentLinks] = useState([]);
    const [paymentSchedule, setPaymentSchedule] = useState(null);
    const [loading, setLoading] = useState(false);
    const [paymentModalVisible, setPaymentModalVisible] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);

    useEffect(() => {
        if (quote?.id) {
            fetchPaymentData();
        }
    }, [quote?.id]);

    const fetchPaymentData = async () => {
        try {
            setLoading(true);
            const [linksResponse, scheduleResponse] = await Promise.all([
                api.getQuotePaymentLinks(quote.id),
                api.getQuotePaymentSchedule(quote.id)
            ]);

            if (linksResponse.success) {
                setPaymentLinks(linksResponse.data.payment_links || []);
            }

            if (scheduleResponse.success) {
                setPaymentSchedule(scheduleResponse.data.payment_schedule);
            }
        } catch (error) {
            console.error('Failed to fetch payment data:', error);
            message.error('Failed to load payment information');
        } finally {
            setLoading(false);
        }
    };

    const handlePayment = (paymentLink) => {
        setSelectedPayment(paymentLink);
        setPaymentModalVisible(true);
    };

    const processPayment = async (paymentData) => {
        try {
            setLoading(true);
            const response = await api.processQuotePayment(quote.id, {
                payment_link_id: selectedPayment.payment_link_id,
                payment_method: selectedPayment.payment_method,
                payment_data: paymentData
            });

            if (response.success) {
                message.success('Payment processed successfully!');
                setPaymentModalVisible(false);
                setSelectedPayment(null);
                fetchPaymentData();
                if (onPaymentSuccess) {
                    onPaymentSuccess(response.data);
                }
            } else {
                message.error(response.message || 'Payment failed');
            }
        } catch (error) {
            console.error('Payment processing failed:', error);
            message.error('Payment processing failed');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'orange',
            completed: 'green',
            failed: 'red',
            expired: 'gray'
        };
        return colors[status] || 'default';
    };

    const getPaymentTypeIcon = (type) => {
        const icons = {
            advance: <DollarOutlined />,
            full: <CreditCardOutlined />,
            installment: <CalendarOutlined />
        };
        return icons[type] || <CreditCardOutlined />;
    };

    const isPaymentExpired = (expiresAt) => {
        return expiresAt && dayjs(expiresAt).isBefore(dayjs());
    };

    const paymentLinkColumns = [
        {
            title: 'Type',
            dataIndex: 'payment_type',
            key: 'payment_type',
            render: (type) => (
                <Space>
                    {getPaymentTypeIcon(type)}
                    <Text strong>{type.charAt(0).toUpperCase() + type.slice(1)} Payment</Text>
                </Space>
            )
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount) => <Text strong>₹{amount.toLocaleString()}</Text>
        },
        {
            title: 'Method',
            dataIndex: 'payment_method',
            key: 'payment_method',
            render: (method) => <Tag color="blue">{method.toUpperCase()}</Tag>
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status, record) => {
                const expired = isPaymentExpired(record.expires_at);
                const finalStatus = expired ? 'expired' : status;
                return (
                    <Tag color={getStatusColor(finalStatus)}>
                        {finalStatus.charAt(0).toUpperCase() + finalStatus.slice(1)}
                    </Tag>
                );
            }
        },
        {
            title: 'Expires',
            dataIndex: 'expires_at',
            key: 'expires_at',
            render: (expiresAt) => {
                if (!expiresAt) return 'Never';
                const expired = isPaymentExpired(expiresAt);
                return (
                    <Text type={expired ? 'danger' : 'secondary'}>
                        {dayjs(expiresAt).format('MMM DD, YYYY')}
                    </Text>
                );
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => {
                const expired = isPaymentExpired(record.expires_at);
                const canPay = record.status === 'pending' && !expired;
                
                return (
                    <Space>
                        <Button 
                            size="small" 
                            icon={<EyeOutlined />}
                            onClick={() => handlePayment(record)}
                            disabled={!canPay}
                        >
                            {canPay ? 'Pay Now' : 'View Details'}
                        </Button>
                    </Space>
                );
            }
        }
    ];

    return (
        <div>
            <Row gutter={[16, 16]}>
                {/* Quote Summary */}
                <Col span={24}>
                    <Card>
                        <Row gutter={[16, 16]}>
                            <Col span={6}>
                                <Statistic
                                    title="Quote Number"
                                    value={quote?.quote_number}
                                    prefix={<CreditCardOutlined />}
                                />
                            </Col>
                            <Col span={6}>
                                <Statistic
                                    title="Total Amount"
                                    value={quote?.total_amount}
                                    prefix="₹"
                                    precision={2}
                                />
                            </Col>
                            <Col span={6}>
                                <Statistic
                                    title="Status"
                                    value={quote?.status}
                                    valueStyle={{ 
                                        color: quote?.status === 'accepted' ? '#3f8600' : '#cf1322' 
                                    }}
                                />
                            </Col>
                            <Col span={6}>
                                <Statistic
                                    title="Event Date"
                                    value={quote?.event_details?.date ? dayjs(quote.event_details.date).format('MMM DD, YYYY') : 'N/A'}
                                />
                            </Col>
                        </Row>
                    </Card>
                </Col>

                {/* Payment Links Section */}
                <Col span={24}>
                    <Card 
                        title={
                            <Space>
                                <CreditCardOutlined />
                                Available Payments
                            </Space>
                        }
                    >
                        <Table
                            columns={paymentLinkColumns}
                            dataSource={paymentLinks}
                            rowKey="id"
                            pagination={false}
                            loading={loading}
                            locale={{ emptyText: 'No payment links available' }}
                        />
                    </Card>
                </Col>

                {/* Payment Schedule Section */}
                {paymentSchedule && (
                    <Col span={24}>
                        <Card 
                            title={
                                <Space>
                                    <CalendarOutlined />
                                    Payment Schedule
                                </Space>
                            }
                        >
                            <Row gutter={[16, 16]}>
                                <Col span={12}>
                                    <Card size="small">
                                        <Text strong>Advance Amount</Text>
                                        <div style={{ fontSize: '18px', color: '#1890ff' }}>
                                            ₹{paymentSchedule.advance_amount?.toLocaleString()}
                                        </div>
                                        <div style={{ color: '#666', fontSize: '12px' }}>
                                            {paymentSchedule.advance_paid_at ? 
                                                `Paid on ${dayjs(paymentSchedule.advance_paid_at).format('MMM DD, YYYY')}` :
                                                'Not paid yet'
                                            }
                                        </div>
                                    </Card>
                                </Col>
                                <Col span={12}>
                                    <Card size="small">
                                        <Text strong>Schedule Status</Text>
                                        <div>
                                            <Tag color={getStatusColor(paymentSchedule.status)}>
                                                {paymentSchedule.status?.toUpperCase()}
                                            </Tag>
                                        </div>
                                        <div style={{ color: '#666', fontSize: '12px' }}>
                                            Created on {dayjs(paymentSchedule.advance_requested_at).format('MMM DD, YYYY')}
                                        </div>
                                    </Card>
                                </Col>
                            </Row>
                            
                            <Divider />
                            
                            <Title level={5}>Payment Milestones</Title>
                            <Timeline>
                                {paymentSchedule.milestones?.map((milestone, index) => (
                                    <Timeline.Item
                                        key={index}
                                        dot={
                                            <Badge 
                                                count={index + 1} 
                                                style={{ backgroundColor: '#1890ff' }} 
                                            />
                                        }
                                    >
                                        <div>
                                            <Text strong>{milestone.name}</Text>
                                            <div>Amount: ₹{milestone.amount?.toLocaleString()}</div>
                                            <div>Due: {dayjs(milestone.due_date).format('MMM DD, YYYY')}</div>
                                            {milestone.description && (
                                                <div style={{ color: '#666' }}>{milestone.description}</div>
                                            )}
                                        </div>
                                    </Timeline.Item>
                                ))}
                            </Timeline>
                        </Card>
                    </Col>
                )}
            </Row>

            {/* Payment Modal */}
            <Modal
                title={`Process ${selectedPayment?.payment_type} Payment`}
                open={paymentModalVisible}
                onCancel={() => setPaymentModalVisible(false)}
                footer={null}
                width={600}
            >
                {selectedPayment && (
                    <div>
                        <Alert
                            message={`Payment Amount: ₹${selectedPayment.amount?.toLocaleString()}`}
                            description={`This is a ${selectedPayment.payment_type} payment for quote ${quote?.quote_number}`}
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                        
                        <div style={{ textAlign: 'center', padding: '20px 0' }}>
                            <Text>Payment processing would be implemented here based on the selected payment method.</Text>
                            <br />
                            <Text type="secondary">
                                For now, this is a demo. In production, this would integrate with Razorpay/Stripe.
                            </Text>
                        </div>

                        <div style={{ textAlign: 'center', marginTop: 20 }}>
                            <Space>
                                <Button 
                                    type="primary" 
                                    loading={loading}
                                    onClick={() => processPayment({ demo: true })}
                                >
                                    Process Payment (Demo)
                                </Button>
                                <Button onClick={() => setPaymentModalVisible(false)}>
                                    Cancel
                                </Button>
                            </Space>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default QuotePaymentViewer;
